import { useSelector, useStore } from 'react-redux';
import { useCallback } from 'react';
import { Dispatch } from 'redux';

import {
    getPaymentTypeFeesThunk,
    getPaymentTypesForOrganizationThunk,
    selectPaymentTypeFeesAsyncState,
    selectPaymentTypesAsyncState,
} from 'common/features/store/duck/organization/duck';
import { selectHomeDetails } from 'common/features/store/duck/home/duck';
import {
    getSavedCreditCardsThunk,
    selectSavedCreditCardsAsyncState,
    getUserAchAccountThunk,
    selectAchAccountAsyncState,
    getDefaultPaymentMethodThunk,
    selectPaymentCreditCards,
} from 'common/features/store/duck/account/duck';
import { APIResponse, APIError } from 'common/api/models';
import { AsyncActionState } from 'common/modules/async-actions/core';
import { isDefined } from 'common/utils';
import { emptyHomeDetailsResponse } from 'common/api/crm/home/models/HomeDetailsResponse';
import { StatusCode } from 'common/api/config';
import { FeaturesState } from 'common/features/featuresReducer';

export interface Loader {
    load: (s: FeaturesState) => (dispatch: Dispatch) => Promise<APIResponse<unknown>>;
    getAsyncState: (s: FeaturesState) => AsyncActionState<unknown, APIError>;
    hasError?: (s: FeaturesState) => boolean;
}

const useFeatureSelector = <TSelected>(selector: (state: FeaturesState) => TSelected) =>
    useSelector<FeaturesState, TSelected>(selector);

export const loaders: Loader[] = [
    {
        load: () => getPaymentTypeFeesThunk(),
        getAsyncState: selectPaymentTypeFeesAsyncState,
    },
    {
        load: () => getSavedCreditCardsThunk(),
        getAsyncState: selectSavedCreditCardsAsyncState,
    },
    {
        load: () => getUserAchAccountThunk(),
        getAsyncState: selectAchAccountAsyncState,
        hasError: (s) => {
            const { error } = selectAchAccountAsyncState(s);
            return Boolean(
                error && error.response && error.response.status !== StatusCode.NotFound
            );
        },
    },
    {
        load: () => getDefaultPaymentMethodThunk(),
        getAsyncState: (s) => s.store.account.achAccountAsyncState,
        hasError: (s) => {
            const { error } = s.store.account.achAccountAsyncState;
            return Boolean(
                error && error.response && error.response.status !== StatusCode.NotFound
            );
        },
    },
    {
        load: (s) => {
            const defaultOrganization = (selectHomeDetails(s) || emptyHomeDetailsResponse)
                .defaultOrganization;
            return getPaymentTypesForOrganizationThunk(
                (defaultOrganization && defaultOrganization.id) || ''
            );
        },
        getAsyncState: selectPaymentTypesAsyncState,
        hasError: (s) => {
            if (!isDefined(selectHomeDetails(s))) {
                return true;
            }
            return Boolean(s.store.organization.paymentTypesAsyncState.error);
        },
    },
];

const selectIsLoading = (rootState: FeaturesState) => {
    return loaders.some(({ getAsyncState }) => getAsyncState(rootState).loading);
};

const selectFirstError = (rootState: FeaturesState) => {
    const maybeErrors = loaders.map(({ getAsyncState }) => getAsyncState(rootState).error);
    return maybeErrors.find(isDefined);
};

export const usePaymentOptions = () => {
    const store = useStore<FeaturesState>();

    const loadPaymentOptionsData = useCallback(async () => {
        for (let i = 0; i < loaders.length; i++) {
            const { load } = loaders[i];
            await load(store.getState())(store.dispatch);
        }
    }, [store]);

    const refreshIfNeeded = useCallback(async (): Promise<void | APIError> => {
        const rootState = store.getState();
        const needsData = (loader: Loader) =>
            loader.hasError
                ? loader.hasError(rootState)
                : isDefined(loader.getAsyncState(rootState).error);
        loaders.filter(needsData).forEach(({ load }) => load(rootState)(store.dispatch));
        if (!selectIsLoading(store.getState())) {
            return;
        }

        return new Promise((resolve) => {
            const unsubscribe = store.subscribe(() => {
                const maybeError = selectFirstError(store.getState());
                if (maybeError || !selectIsLoading(store.getState())) {
                    unsubscribe();
                    resolve(maybeError);
                }
            });
        });
    }, [store]);

    const hasError = useCallback(() => {
        const rootState = store.getState();

        return loaders.some((loader) => {
            if (loader.hasError) {
                return loader.hasError(rootState);
            }

            return loader.getAsyncState(rootState).error;
        });
    }, [store]);

    return {
        hasError,
        refreshIfNeeded,
        loadPaymentOptionsData,
        isLoading: useFeatureSelector(selectIsLoading),
        paymentTypeFees: useFeatureSelector((s) => s.store.organization.paymentTypeFees),
        paymentTypes: useFeatureSelector((s) => s.store.organization.paymentTypes),
        savedCreditCards: useFeatureSelector(selectPaymentCreditCards),
        achAccount: useFeatureSelector((s) => s.store.account.achAccount),
    };
};
